/**
 * Shared helper for agent context menu services.
 * Provides common patterns: open workspace with subView, delete with confirm dialog,
 * and toggle alert enabled state.
 */

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import type { JobSchedulerSubView, AgentEditTarget } from '../../workspace/core/types/workspaceTypes';
import { toast } from '../../../components/ui/toaster';
import { useConfirmDialogStore } from './confirmDialogStore';

export function openAgentWorkspace(connectionId: string, subView: JobSchedulerSubView, editTarget?: AgentEditTarget) {
  const { addTab } = useWorkspaceStore.getState();
  addTab({
    type: 'job-scheduler',
    title: 'Agent Jobs',
    connectionInfo: { connectionId },
    content: { subView, editTarget },
  });
}

export async function deleteAgentObject(
  connectionId: string,
  objectType: string,
  objectName: string,
  deleteFn: (args: { connectionId: string; name: string }) => Promise<{ success: boolean; error?: string }>,
  onRefresh: () => void,
) {
  if (!window.electronAPI?.jobScheduler) {
    toast.error({ title: 'Job Scheduler not available' });
    return;
  }

  const confirmed = await useConfirmDialogStore.getState().requestConfirm(
    `Delete ${objectType}`,
    `Are you sure you want to delete "${objectName}"? This action cannot be undone.`,
  );
  if (!confirmed) return;

  try {
    const result = await deleteFn({ connectionId, name: objectName });
    if (result.success) {
      toast.success({ title: `${objectType} "${objectName}" deleted` });
      onRefresh();
    } else {
      toast.error({ title: 'Delete failed', description: result.error });
    }
  } catch (error) {
    toast.error({
      title: 'Delete failed',
      description: error instanceof Error ? error.message : 'Unknown error',
    });
  }
}

export async function toggleAlertEnabled(
  connectionId: string,
  alertName: string,
  currentlyEnabled: boolean,
  onRefresh: () => void,
) {
  const api = window.electronAPI?.jobScheduler;
  if (!api) {
    toast.error({ title: 'Job Scheduler not available' });
    return;
  }

  try {
    const result = await api.toggleAlertEnabled({
      connectionId,
      name: alertName,
      enabled: !currentlyEnabled,
    });

    if (result?.success) {
      const action = !currentlyEnabled ? 'enabled' : 'disabled';
      toast.success({ title: `Alert "${alertName}" ${action}` });
      onRefresh();
    } else {
      toast.error({ title: 'Failed to toggle alert', description: result?.error });
    }
  } catch (error) {
    toast.error({
      title: 'Failed to toggle alert',
      description: error instanceof Error ? error.message : 'Unknown error',
    });
  }
}
